import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productFormComponent = productGrid.querySelector('product-form-component');
    const promoText = productGrid.querySelector('.product-details .product-promo-text');
    if (productFormComponent && promoText) {
      productFormComponent.prepend(promoText);
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // The modal content is injected via morph, so inline scripts are not guaranteed to execute.
    // Re-running them here keeps quick-add swatches behavior aligned with PDP logic.
    this.#executeInlineScripts(modalContent);
    this.#configureModalSlideshows(modalContent);
    this.#bindModalSlideshowSync(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Executes inline scripts in dynamically injected modal content.
   * @param {Element} modalContent - The modal content element
   */
  #executeInlineScripts(modalContent) {
    const scripts = Array.from(modalContent.querySelectorAll('script')).filter(
      (script) => script.getAttribute('type') !== 'application/json'
    );

    for (const script of scripts) {
      const executableScript = document.createElement('script');

      for (const attr of script.attributes) {
        executableScript.setAttribute(attr.name, attr.value);
      }

      executableScript.textContent = script.textContent;
      script.replaceWith(executableScript);
    }
  }

  /**
   * Ensures quick-add slideshow controls match active slides and loop infinitely.
   * @param {Element} modalContent - The modal content element
   */
  #configureModalSlideshows(modalContent) {
    const slideshows = Array.from(modalContent.querySelectorAll('slideshow-component'));

    for (const slideshow of slideshows) {
      if (!(slideshow instanceof HTMLElement)) continue;

      slideshow.setAttribute('infinite', '');
      this.#hideModalSlideshowDots(slideshow);
      this.#ensureModalSlideshowArrows(slideshow);
      this.#enableMobileInfiniteScroll(slideshow);
    }
  }

  /**
   * Hides dot controls inside quick-add modal slideshows.
   * @param {HTMLElement} slideshow
   */
  #hideModalSlideshowDots(slideshow) {
    const dotsLists = slideshow.querySelectorAll('.slideshow-controls__dots');
    dotsLists.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  /**
   * Injects prev/next arrow buttons into the slideshow container if not already present.
   * Arrows call the native slideshow previous()/next() methods.
   * @param {HTMLElement} slideshow
   */
  #ensureModalSlideshowArrows(slideshow) {
    if (slideshow.querySelector('.quick-add-arrow')) return;

    const container = slideshow.querySelector('slideshow-container');
    if (!(container instanceof HTMLElement)) return;

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'quick-add-arrow quick-add-arrow--prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`;

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'quick-add-arrow quick-add-arrow--next';
    next.setAttribute('aria-label', 'Next image');
    next.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;

    prev.addEventListener('click', () => {
      /** @type {any} */ (slideshow).previous?.();
    });
    next.addEventListener('click', () => {
      /** @type {any} */ (slideshow).next?.();
    });

    container.appendChild(prev);
    container.appendChild(next);
  }

  /**
   * Enables seamless infinite swipe on mobile by:
   * 1. Cloning the first slide after the last and the last slide before the first.
   * 2. After each swipe finishes snapping (scrollend), silently teleporting back to
   *    the real equivalent position — invisible because the clone looks identical.
   * @param {HTMLElement} slideshow - The slideshow component
   */
  #enableMobileInfiniteScroll(slideshow) {
    if (window.matchMedia('(min-width: 750px)').matches) return;

    const scroller = slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;
    if (scroller.dataset.quickAddClonesBound === 'true') return;
    scroller.dataset.quickAddClonesBound = 'true';

    const realSlides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide:not([data-clone])'));
    if (realSlides.length < 2) return;

    const first = realSlides[0];
    const last = realSlides[realSlides.length - 1];
    if (!first || !last) return;

    // Prepend clone of last real slide — user swiping back from slide 1 sees the last slide seamlessly.
    const leadingClone = /** @type {HTMLElement} */ (last.cloneNode(true));
    leadingClone.setAttribute('aria-hidden', 'true');
    leadingClone.setAttribute('data-clone', 'leading');
    leadingClone.removeAttribute('ref');
    scroller.prepend(leadingClone);

    // Append clone of first real slide — user swiping forward from last slide sees slide 1 seamlessly.
    const trailingClone = /** @type {HTMLElement} */ (first.cloneNode(true));
    trailingClone.setAttribute('aria-hidden', 'true');
    trailingClone.setAttribute('data-clone', 'trailing');
    trailingClone.removeAttribute('ref');
    scroller.append(trailingClone);

    // Scroll to real slide 1 (past the single leading clone) without animation.
    requestAnimationFrame(() => {
      scroller.scrollLeft = scroller.clientWidth;
    });

    // After the browser finishes snapping, silently teleport to the real position.
    // The clone looks identical to the real slide so the user never sees a jump.
    const resetAfterSnap = () => {
      const sw = scroller.clientWidth;
      const sl = scroller.scrollLeft;
      const realWidth = realSlides.length * sw;
      const firstRealOffset = sw;             // after the 1 leading clone
      const lastRealOffset = sw * realSlides.length; // last real slide

      if (sl >= lastRealOffset + sw) {
        // User swiped forward past last real slide into trailing clone → go to real slide 1
        scroller.scrollLeft = firstRealOffset;
      } else if (sl < firstRealOffset * 0.5) {
        // User swiped back before real slide 1 into leading clone → go to real last slide
        scroller.scrollLeft = lastRealOffset;
      }
    };

    // scrollend fires after CSS snap is complete — no visible reposition on screen.
    const scrollerEl = /** @type {EventTarget} */ (scroller);
    if ('onscrollend' in scroller) {
      scrollerEl.addEventListener('scrollend', resetAfterSnap, { passive: true });
    } else {
      // Fallback: fire ~100 ms after last scroll event (snap is usually done by then).
      let t = 0;
      scrollerEl.addEventListener(
        'scroll',
        () => {
          clearTimeout(t);
          t = window.setTimeout(resetAfterSnap, 120);
        },
        { passive: true }
      );
    }
  }

  /**
   * Keeps dots in sync after variant updates in the quick-add modal.
   * @param {Element} modalContent - The modal content element
   */
  #bindModalSlideshowSync(modalContent) {
    const modalElement = /** @type {HTMLElement} */ (modalContent);

    if (modalElement.dataset.quickAddSlideshowSyncBound === 'true') return;
    modalElement.dataset.quickAddSlideshowSyncBound = 'true';

    modalElement.addEventListener(ThemeEvents.variantUpdate, () => {
      // Delay to let media-gallery.js finish replacing the gallery DOM before re-syncing dots/slides.
      setTimeout(() => {
        this.#configureModalSlideshows(modalElement);
      }, 50);
    });
  }

  /**
   * Aligns dot controls with the actual number of real (non-clone) slides.
   * Called after variant filtering so the count reflects current variant images only.
   * @param {HTMLElement} slideshow - The slideshow component
   */
  #syncSlideshowDots(slideshow) {
    // Count only real slides — clones added by #enableMobileInfiniteScroll carry [data-clone]
    const realSlides = Array.from(
      slideshow.querySelectorAll('slideshow-slides > slideshow-slide:not([data-clone])')
    );
    const targetCount = realSlides.length;

    const dotsLists = Array.from(slideshow.querySelectorAll('.slideshow-controls__dots'));

    for (const dotsList of dotsLists) {
      if (!(dotsList instanceof HTMLOListElement)) continue;

      const dotItems = Array.from(dotsList.querySelectorAll(':scope > li'));

      if (!targetCount) {
        dotsList.innerHTML = '';
        continue;
      }

      // Remove surplus dots (e.g. variant has 4 images but dots were rendered for 8)
      if (dotItems.length > targetCount) {
        dotItems.slice(targetCount).forEach((item) => item.remove());
      }

      // Add missing dots (rare — variant has more images than initial server render)
      if (dotItems.length < targetCount) {
        const template = dotItems[0];
        if (!template) continue;
        for (let i = dotItems.length; i < targetCount; i++) {
          const newItem = /** @type {HTMLElement} */ (template.cloneNode(true));
          const btn = newItem.querySelector('button');
          if (btn) {
            btn.setAttribute('on:click', `/select/${i}`);
            btn.setAttribute('aria-selected', 'false');
            btn.textContent = String(i + 1);
          }
          dotsList.appendChild(newItem);
        }
      }
    }
  }

  /**
   * Keeps the modal's hidden native size inputs aligned with the visible size swatches.
   * @param {Element} modalContent - The modal content element
   */
  #bindModalSizeSync(modalContent) {
    const modalElement = /** @type {HTMLElement} */ (modalContent);

    if (modalElement.dataset.quickAddSizeSyncBound === 'true') return;

    modalElement.dataset.quickAddSizeSyncBound = 'true';

    modalElement.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== 'base_size' && target.name !== 'width_radio') return;

      setTimeout(() => {
        this.#syncModalSizeSelection(modalElement);
      }, 0);
    });
  }

  /**
   * Synchronizes the hidden size radios used by the variant picker.
   * @param {Element} modalContent - The modal content element
   */
  #syncModalSizeSelection(modalContent) {
    const modalElement = /** @type {HTMLElement} */ (modalContent);
    const widthRadio = modalElement.querySelector('input[name="width_radio"]:checked');
    const sizeRadio = modalElement.querySelector('input[name="base_size"]:checked');

    if (!(widthRadio instanceof HTMLInputElement) || !(sizeRadio instanceof HTMLInputElement)) return;

    const widthCode = (widthRadio.dataset.widthCode || '').toUpperCase();
    const baseSizeValue = sizeRadio.value;
    const nativeInputs = Array.from(modalElement.querySelectorAll('.shopify-real-size-input')).filter(
      (input) => input instanceof HTMLInputElement
    );

    if (!nativeInputs.length) return;

    /** @param {string} value */
    const normalize = (value) => (value || '').toString().trim().replace(/\s+/g, ' ').toUpperCase();
    const targetValues = [normalize(`${baseSizeValue}${widthCode}`), normalize(`${baseSizeValue} ${widthCode}`)];

    if (widthCode === 'M') {
      targetValues.push(normalize(baseSizeValue));
    }

    const matchedNativeInput = nativeInputs.find((input) => targetValues.includes(normalize(input.value)));
    if (!(matchedNativeInput instanceof HTMLInputElement)) return;

    let changed = false;

    for (const input of nativeInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      if (input === matchedNativeInput) {
        if (!input.checked) {
          input.checked = true;
          changed = true;
        }
        continue;
      }

      if (input.checked) {
        input.checked = false;
        changed = true;
      }
    }

    if (changed) {
      matchedNativeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Disables unavailable swatches in the quick-add modal only.
   * @param {Element} modalContent - The modal content element
   */
  #setUnavailableSwatchesState(modalContent) {
    const swatchInputs = Array.from(modalContent.querySelectorAll('input[type="radio"][data-option-available]'));

    for (const input of swatchInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const isAvailable = input.dataset.optionAvailable !== 'false';
      const label = input.closest('.variant-option__button-label');

      input.disabled = !isAvailable;
      if (isAvailable) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
      label?.classList.toggle('is-disabled', !isAvailable);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = Array.from(modalContent.querySelectorAll('input[type="radio"][data-variant-id]'));
    const availableInputsByName = new Map();
    let matchedInput = null;

    for (const input of modalInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      if (input.dataset.optionAvailable !== 'false' && !availableInputsByName.has(input.name)) {
        availableInputsByName.set(input.name, input);
      }

      if (input.dataset.variantId === selectedVariantId) {
        matchedInput = input;
      }
    }

    const targetInput =
      matchedInput && matchedInput.dataset.optionAvailable !== 'false'
        ? matchedInput
        : matchedInput
        ? availableInputsByName.get(matchedInput.name) || null
        : modalInputs.find((input) => input instanceof HTMLInputElement && input.dataset.optionAvailable !== 'false') || null;

    if (targetInput instanceof HTMLInputElement && !targetInput.checked) {
      targetInput.checked = true;
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent instanceof HTMLElement) {
      modalContent.replaceChildren();
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
